<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog customer-visibility-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-visibility-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="customer-visibility-modal-title">{{ text.detail.visibilityModal.title }}</h3>
          <p>{{ text.detail.visibilityModal.subtitle }}</p>
        </div>

        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.detail.visibilityModal.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content customer-visibility-modal__content">
        <form class="app-modal__form" @submit.prevent="emitSubmit">
          <section class="app-modal__section">
            <header class="app-modal__section-head">
              <h4>{{ normalizedCustomerName }}</h4>
            </header>

            <div class="customer-visibility-modal__options">
              <button
                v-for="option in visibilityOptions"
                :key="option.value"
                type="button"
                class="customer-visibility-option"
                :class="{ 'customer-visibility-option--active': selectedVisibility === option.value }"
                @click="selectVisibility(option.value)"
              >
                <span class="customer-visibility-option__checkbox" :class="{ 'is-checked': selectedVisibility === option.value }">
                  <i class="fa fa-check" aria-hidden="true"></i>
                </span>
                <span class="customer-visibility-option__body">
                  <span class="customer-visibility-option__title">{{ option.title }}</span>
                  <span class="customer-visibility-option__subtitle">{{ option.subtitle }}</span>
                </span>
              </button>
            </div>

            <div v-if="showUsersSelector" class="app-modal-field full-width customer-visibility-modal__selector">
              <span>{{ usersSelectorLabel }}</span>
              <multiselect
                v-model="selectedUsers"
                :options="normalizedUsersOptions"
                :multiple="true"
                track-by="id"
                label="name"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :show-labels="false"
                :searchable="true"
                :allow-empty="true"
                :max-height="180"
                open-direction="down"
                :placeholder="''"
                class="app-filter-select app-select app-select--filters"
              >
                <template #selection="{ values, isOpen }">
                  <span v-if="!isOpen" class="multiselect__single">
                    {{ usersSelectionLabel(values) }}
                  </span>
                </template>
                <template #option="props">
                  <div class="customer-visibility-modal__user-option">
                    <span>{{ props.option.name }}</span>
                    <small v-if="props.option.email">{{ props.option.email }}</small>
                  </div>
                </template>
                <template #noResult>
                  <span>{{ text.detail.visibilityModal.noUsers }}</span>
                </template>
                <template #noOptions>
                  <span>{{ text.detail.visibilityModal.noUsers }}</span>
                </template>
              </multiselect>
            </div>

            <div v-else class="customer-visibility-modal__note">
              <i class="fa fa-users" aria-hidden="true"></i>
              <p>{{ text.detail.visibilityModal.publicNote }}</p>
            </div>
          </section>

          <footer class="app-modal__footer">
            <div class="app-modal__footer-right">
              <button type="button" class="btn btn-light" :disabled="submitting" @click="emitClose">
                {{ text.detail.visibilityModal.actions.cancel }}
              </button>
              <button type="submit" class="btn btn-primary" :disabled="submitting || loadingUsers">
                {{ submitting ? text.detail.visibilityModal.actions.saving : text.detail.visibilityModal.actions.save }}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');

export default {
  name: 'CustomerVisibilityModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    customerName: {
      type: String,
      default: '',
    },
    usersOptions: {
      type: Array,
      default: () => [],
    },
    initialVisibility: {
      type: String,
      default: 'public',
    },
    initialPrivateUserIds: {
      type: Array,
      default: () => [],
    },
    initialHiddenUserIds: {
      type: Array,
      default: () => [],
    },
    loadingUsers: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'submit'],
  data() {
    return {
      text,
      selectedVisibility: 'public',
      privateSelectedUserIds: [],
      hiddenSelectedUserIds: [],
    };
  },
  computed: {
    normalizedCustomerName() {
      const value = String(this.customerName || '').trim();
      return value !== '' ? value : this.text.common.noName;
    },
    normalizedUsersOptions() {
      return (Array.isArray(this.usersOptions) ? this.usersOptions : [])
        .map((user) => ({
          id: Number(user?.id || 0),
          name: String(user?.name || '').trim(),
          email: String(user?.email || '').trim(),
        }))
        .filter((user) => user.id > 0 && user.name !== '');
    },
    visibilityOptions() {
      return [
        {
          value: 'public',
          title: this.text.detail.visibilityModal.options.public.title,
          subtitle: this.text.detail.visibilityModal.options.public.subtitle,
        },
        {
          value: 'private',
          title: this.text.detail.visibilityModal.options.private.title,
          subtitle: this.text.detail.visibilityModal.options.private.subtitle,
        },
        {
          value: 'hidden',
          title: this.text.detail.visibilityModal.options.hidden.title,
          subtitle: this.text.detail.visibilityModal.options.hidden.subtitle,
        },
      ];
    },
    showUsersSelector() {
      return this.selectedVisibility === 'private' || this.selectedVisibility === 'hidden';
    },
    usersSelectorLabel() {
      return this.selectedVisibility === 'private'
        ? this.text.detail.visibilityModal.usersSelectorPrivate
        : this.text.detail.visibilityModal.usersSelectorHidden;
    },
    selectedUsers: {
      get() {
        const selectedIds = this.selectedVisibility === 'private'
          ? this.privateSelectedUserIds
          : this.hiddenSelectedUserIds;

        const selectedSet = new Set(selectedIds.map((id) => Number(id || 0)));
        return this.normalizedUsersOptions.filter((user) => selectedSet.has(user.id));
      },
      set(values) {
        const selectedIds = (Array.isArray(values) ? values : [])
          .map((user) => Number(user?.id || 0))
          .filter((id) => id > 0);

        if (this.selectedVisibility === 'private') {
          this.privateSelectedUserIds = selectedIds;
          return;
        }

        this.hiddenSelectedUserIds = selectedIds;
      },
    },
  },
  watch: {
    show(visible) {
      if (!visible) {
        return;
      }

      this.resetFromProps();
    },
  },
  methods: {
    usersSelectionLabel(values) {
      const selected = Array.isArray(values) ? values : [];

      if (selected.length <= 0) {
        return this.text.detail.visibilityModal.usersPlaceholder;
      }

      if (selected.length === 1) {
        return String(selected[0]?.name || '').trim() || this.text.detail.visibilityModal.usersPlaceholder;
      }

      return `${selected.length} seleccionados`;
    },
    selectVisibility(value) {
      const normalized = String(value || '').trim().toLowerCase();
      if (!['public', 'private', 'hidden'].includes(normalized)) {
        return;
      }

      this.selectedVisibility = normalized;
    },
    resetFromProps() {
      const visibility = String(this.initialVisibility || 'public').trim().toLowerCase();
      this.selectedVisibility = ['private', 'hidden'].includes(visibility) ? visibility : 'public';
      this.privateSelectedUserIds = (Array.isArray(this.initialPrivateUserIds) ? this.initialPrivateUserIds : [])
        .map((id) => Number(id || 0))
        .filter((id) => id > 0);
      this.hiddenSelectedUserIds = (Array.isArray(this.initialHiddenUserIds) ? this.initialHiddenUserIds : [])
        .map((id) => Number(id || 0))
        .filter((id) => id > 0);
    },
    emitClose() {
      if (this.submitting) {
        return;
      }

      this.$emit('close');
    },
    emitSubmit() {
      if (this.submitting || this.loadingUsers) {
        return;
      }

      const userIds = this.selectedVisibility === 'private'
        ? this.privateSelectedUserIds
        : (this.selectedVisibility === 'hidden' ? this.hiddenSelectedUserIds : []);

      this.$emit('submit', {
        visibility: this.selectedVisibility,
        user_ids: userIds,
      });
    },
  },
};
</script>

<style scoped>
.customer-visibility-modal__dialog {
  max-width: 760px;
  height: auto;
  max-height: calc(100vh - 48px);
  overflow: visible;
}

.customer-visibility-modal__content {
  min-height: auto;
  overflow: visible;
}

.customer-visibility-modal__content :deep(.app-modal__form),
.customer-visibility-modal__content :deep(.app-modal__section),
.customer-visibility-modal__content :deep(.app-modal-field) {
  overflow: visible;
}

.customer-visibility-modal__content :deep(.app-filter-select .multiselect__content-wrapper) {
  z-index: 2405;
}

.customer-visibility-modal__options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.customer-visibility-option {
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.42);
  border-radius: 12px;
  background: #f8fafc;
  padding: 12px;
  text-align: left;
  display: flex;
  gap: 10px;
  transition: all 0.2s ease;
}

.customer-visibility-option:hover {
  border-color: rgba(0, 112, 205, 0.48);
  background: #f2f8ff;
}

.customer-visibility-option--active {
  border-color: rgba(0, 112, 205, 0.72);
  background: linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%);
  box-shadow: 0 8px 24px -18px rgba(0, 112, 205, 0.6);
}

.customer-visibility-option__checkbox {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 1px solid rgba(100, 116, 139, 0.6);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  color: transparent;
  font-size: 10px;
  flex-shrink: 0;
}

.customer-visibility-option__checkbox.is-checked {
  border-color: #0070cd;
  background: #0070cd;
  color: #ffffff;
}

.customer-visibility-option__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.customer-visibility-option__title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.customer-visibility-option__subtitle {
  font-size: 12px;
  line-height: 18px;
  color: #475569;
}

.customer-visibility-modal__selector {
  margin-top: 12px;
}

.customer-visibility-modal__user-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.customer-visibility-modal__user-option small {
  color: #64748b;
  font-size: 11px;
}

.customer-visibility-modal__note {
  margin-top: 12px;
  border: 1px solid rgba(16, 185, 129, 0.32);
  border-radius: 10px;
  background: #f0fdf4;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.customer-visibility-modal__note i {
  color: #047857;
  font-size: 14px;
}

.customer-visibility-modal__note p {
  margin: 0;
  color: #065f46;
  font-size: 12px;
  line-height: 18px;
}

@media (max-width: 860px) {
  .customer-visibility-modal__options {
    grid-template-columns: 1fr;
  }
}
</style>
